
import { useState } from 'react';
import Editor, { type BeforeMount, type OnMount } from '@monaco-editor/react';
import { blackColor } from '../../utils/colors';
import styles from '../../assets/styles/componentStyles/jsonTextEditor.module.scss';


interface JsonTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  readOnlyMessage?: string;
  // Shown like a textarea placeholder while the editor is empty
  placeholder?: string;
}

const JSON_EDITOR_THEME = 'json-text-editor-theme';
const EDITOR_PADDING_TOP = 12;

const JsonTextEditor = ({
  value,
  onChange,
  height = '300px',
  readOnly = false,
  readOnlyMessage = 'This JSON cannot be edited',
  placeholder,
}: JsonTextEditorProps) => {
  const [localValue, setLocalValue] = useState(
    value ?? '{\n  \n}'
  );
  // Left edge of the text area (after Monaco's gutter), so the placeholder lines up with the typed text
  const [placeholderLeft, setPlaceholderLeft] = useState<number>(0);

  const currentValue = value ?? localValue;
  const flagShowPlaceholder = !!placeholder && currentValue.length === 0;

  const handleChange = (newValue: string | undefined) => {
    const updatedValue = newValue ?? '';

    setLocalValue(updatedValue);
    onChange?.(updatedValue);
  };

  // All text, brackets and separators are black. Only the keys are a bit lighter (black with reduced opacity).
  const handleBeforeMount: BeforeMount = (monaco) => {
    const keyColor = blackColor.replace('#', '');
    const valueColor = '525252';

    monaco.editor.defineTheme(JSON_EDITOR_THEME, {
      base: 'vs',
      inherit: true,
      rules: [
        { token: '', foreground: keyColor },
        { token: 'string.key.json', foreground: keyColor },
        { token: 'string.value.json', foreground: valueColor },
        { token: 'number.json', foreground: valueColor },
        { token: 'keyword.json', foreground: valueColor },
      ],
      colors: {
        'editor.foreground': blackColor,
        // Bracket pair colors (used if colorization is ever turned back on) and mismatched brackets
        'editorBracketHighlight.foreground1': blackColor,
        'editorBracketHighlight.foreground2': blackColor,
        'editorBracketHighlight.foreground3': blackColor,
        'editorBracketHighlight.foreground4': blackColor,
        'editorBracketHighlight.foreground5': blackColor,
        'editorBracketHighlight.foreground6': blackColor,
        'editorBracketHighlight.unexpectedBracket.foreground': blackColor,
      },
    });
  };

  const handleEditorMount: OnMount = (editorInstance) => {
    setPlaceholderLeft(editorInstance.getLayoutInfo().contentLeft);
    editorInstance.onDidLayoutChange((layoutInfo: { contentLeft: number }) => setPlaceholderLeft(layoutInfo.contentLeft));

    // Format the JSON using the editor's formatting action.
    editorInstance.addAction({
      id: 'format-json',
      label: 'Format JSON',
      keybindings: [
        2048 | 1024 | 49, // Shift + Alt + F (Windows)
      ],
      run: (instance: any) => {
        instance.getAction('editor.action.formatDocument')?.run();
      },
    });
  };

  return (
    <div className={styles.jsonTextEditor}>
      {/* Monaco sets pixel widths inline, which stops parent flex/grid layouts from shrinking.
          Taking it out of flow (absolute) means it only fills the box and never sizes it. */}
      <div className={styles.jsonTextEditorSizer} style={{ height }}>
        <div className={styles.jsonTextEditorFill}>
          <Editor
            height="100%"
            width="100%"
            language="json"
            theme={JSON_EDITOR_THEME}
            value={currentValue}
            onChange={handleChange}
            beforeMount={handleBeforeMount}
            onMount={handleEditorMount}
            options={{
              // Basic editor behavior
              minimap: { enabled: false },
              lineNumbers: 'off',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              // Render hover/tooltip widgets outside the editor box, so the wrapper's overflow: hidden doesn't clip them
              fixedOverflowWidgets: true,
              tabSize: 8,
              insertSpaces: true,
              autoIndent: 'full',

              // Remove the border lines above/below the current line
              renderLineHighlight: 'none',
              hideCursorInOverviewRuler: true,
              overviewRulerBorder: false,
              // Remove the right-edge markers (bracket/cursor position) from the scrollbar
              // overviewRulerLanes: 0,

              // Allow long lines to wrap
              wordWrap: 'off',
              wrappingIndent: 'indent',
              // Same size as the app's default scrollbar (global.module.scss); its color/shape is set in jsonTextEditor.module.scss
              scrollbar: {
                horizontal: 'auto',
                verticalScrollbarSize: 5,
                horizontalScrollbarSize: 6,
                useShadows: false,
                alwaysConsumeMouseWheel: false,
              },

              // Disable the upper sticky object/array block
              stickyScroll: {
                enabled: false,
              },

              // Automatically close brackets and quotes
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              autoSurround: 'languageDefined',
              // Off: it colors each nesting level of brackets differently, overriding the black bracket color of the theme
              bracketPairColorization: { enabled: false },

              // Disable autocomplete and suggestions
              quickSuggestions: false,
              suggestOnTriggerCharacters: false,
              wordBasedSuggestions: 'off',
              inlineSuggest: { enabled: false },
              parameterHints: { enabled: false },            

              // Keep normal editing behavior
              formatOnPaste: true,
              formatOnType: false,
              readOnly,
              // Text of the tooltip shown when the user types in a read-only editor
              readOnlyMessage: { value: readOnlyMessage },
              contextmenu: true,
              // Thin, standard text caret (Monaco's default is 2px wide)
              cursorStyle: 'line',
              cursorWidth: 1,
              fontSize: 14,
              lineHeight: 22,
              padding: { top: EDITOR_PADDING_TOP, bottom: 12 },
            }}
          />
        </div>
        {flagShowPlaceholder &&
          <div
            className={styles.jsonTextEditorPlaceholder}
            style={{ top: EDITOR_PADDING_TOP, left: placeholderLeft }}
            aria-hidden="true"
          >
            {placeholder}
          </div>
        }
      </div>
    </div>
  );
};

export default JsonTextEditor;
